import React, { createContext, useContext, useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useSortableItems } from "./useSortableItems";
import useProcessFile from "./useProcessFiles";
import getMimeTypeFromExtension from "@rf-helper/mime-trasform";
import toast from "react-hot-toast";
import { IFileData } from "@rf-l1/file/helpers/create-file-data";
import {
  RomaExtras,
  RomaProviderProps,
  TOAST_OPTIONS_ROMA
} from "./models/Roma.model";
import { AllowedFileExtensions } from "@rf-l1/file/model/types.model";

const RomaContext = createContext<RomaContextProps | undefined>(undefined);
interface RomaContextProps {
  data: IFileData[];
  onDataChange: (newState: IFileData[], extras?: RomaExtras) => void;
  isDragActive: boolean;
  handleOpenDropzone: () => void;
  getRootProps: () => React.HTMLAttributes<HTMLDivElement>;
  getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
  getContentSortableProps: () => React.HTMLAttributes<HTMLDivElement>;
  getSortableItemProps: (index: number) => React.HTMLAttributes<HTMLDivElement>;
  isDragging: boolean;
  onDeleteFile: (index: number) => void;
  onDeleteAllFiles: () => void;
  isDeletingAll: boolean;
  accept: AllowedFileExtensions[];
  maxSize: number;
  minSize: number;
  direction: "horizontal" | "vertical";
  label?: string;
  name?: string;
  id?: string;
  invalid?: boolean;
  onBlur?: () => void;
  uploadMethod?: (data: IFileData) => Promise<void>;
  disabled?: boolean;
}

export function useRoma() {
  const context = useContext(RomaContext);
  if (!context) {
    throw new Error("useRoma debe ser usado dentro de un RomaProvider");
  }
  return context;
}

export function RomaProvider(props: RomaProviderProps) {
  const {
    children,
    initialState,
    onDataChange,
    accept,
    isMultiple,
    maxSize,
    minSize,
    direction,
    name,
    id,
    invalid,
    onBlur,
    uploadMethod,
    disabled,
    label,
    onAfterDeleteAllFiles
  } = props;

  // Always coerce to array to avoid runtime issues when parents send null/other
  const safeInitialState: IFileData[] = Array.isArray(initialState)
    ? initialState
    : [];

  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const processFiles = useProcessFile({
    maxSize: maxSize ?? 10,
    minSize: minSize ?? 0,
    allowedTypes: accept ?? [],
    multiple: isMultiple ?? true,
    oldFiles: safeInitialState
  });

  const handleChange = useCallback(
    (newState: IFileData[], extras?: RomaExtras) => {
      onDataChange?.(newState, extras);
    },
    [onDataChange]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const toastId = "process-files";
      toast.loading("Procesando archivos...", { id: toastId });

      try {
        // Pasar siempre el estado más reciente para evitar duplicados al caer múltiples archivos
        const files = await processFiles(acceptedFiles, safeInitialState);
        // toast.success("Archivos procesados correctamente", TOAST_OPTIONS_ROMA);
        handleChange(files, { event: "append" });
      } catch (error) {
      } finally {
        toast.dismiss(toastId);
      }
    },
    [processFiles, handleChange, safeInitialState]
  );

  const { getContentSortableProps, getSortableItemProps, isDragging } =
    useSortableItems({
      value: safeInitialState,
      onChange: (newValue, oldIndex, newIndex) =>
        handleChange(newValue, {
          oldIndex,
          newIndex,
          event: "update"
        }),
      direction
    });

  const dropzoneConfig = useMemo(
    () => ({
      onDrop,
      noClick: isDragging,
      noKeyboard: true,
      disabled: isDragging,
      accept: getMimeTypeFromExtension(accept ?? []),
      multiple: isMultiple ?? true
    }),
    [onDrop, isDragging, accept, isMultiple]
  );

  const { getRootProps, getInputProps, isDragActive, open } =
    useDropzone(dropzoneConfig);

  const onDeleteFile = useCallback(
    (index: number) => {
      if (props.onDeleteFile) {
        props.onDeleteFile(index);
        return;
      }
      handleChange(
        safeInitialState.filter((_, i) => i !== index),
        {
          event: "remove",
          oldIndex: index
        }
      );
    },
    [handleChange, safeInitialState, props.onDeleteFile]
  );

  const onDeleteAllFiles = useCallback(async () => {
    if (onAfterDeleteAllFiles) {
      const snapshot = [...safeInitialState];
      const api = { set: (files: IFileData[]) => handleChange(files, { event: "remove" }) };
      try {
        setIsDeletingAll(true);
        await onAfterDeleteAllFiles(snapshot, api);
      } catch (_) {
        // mantener estado si falla
      } finally {
        setIsDeletingAll(false);
      }
      return;
    }
    try {
      setIsDeletingAll(true);
      handleChange([], { event: "remove" });
    } finally {
      setIsDeletingAll(false);
    }
  }, [handleChange, onAfterDeleteAllFiles, safeInitialState]);

  const contextValue = useMemo(
    () => ({
      data: safeInitialState,
      onDataChange: handleChange,
      isDragActive,
      handleOpenDropzone: open,
      getRootProps,
      getInputProps,
      getContentSortableProps,
      getSortableItemProps,
      isDragging,
      onDeleteFile,
      onDeleteAllFiles,
      isDeletingAll,
      accept,
      maxSize,
      minSize,
      direction,
      name,
      id,
      invalid,
      onBlur,
      uploadMethod,
      disabled,
      label
    }),
    [
      safeInitialState,
      handleChange,
      isDragActive,
      open,
      getRootProps,
      getInputProps,
      getContentSortableProps,
      getSortableItemProps,
      isDragging,
      onDeleteFile,
      onDeleteAllFiles,
      isDeletingAll,
      accept,
      maxSize,
      minSize,
      direction,
      name,
      id,
      invalid,
      disabled,
      label
    ]
  );
  return (
    <RomaContext.Provider value={contextValue as RomaContextProps}>
      {children}
    </RomaContext.Provider>
  );
}
